import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import Box from '@mui/material/Box'
import { getObjectsWithFilter, getObject, editObject } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import { useAuth } from '../../../context/AuthContext'
import { useLocation } from 'react-router-dom'
import Form from '../../form/Form'
import InvoicePDF from './InvoicePDF'
import { buildInvoicePayload, convertDocumentAndUploadFile } from '../../../utils/documentManager'
import { getInvoiceFields } from './InvoiceFields'
import useSaveEntity from '../../hooks/useSaveEntity'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import Toolbar from '../../toolbar/Toolbar'

const ROUTES = {
  EDIT: '/invoices/edit',
  VIEW: '/invoices/view',
  ADD: '/invoices/add'
}

const EditInvoice = () => {
  const history = useNavigate()
  const { user } = useAuth()
  const location = useLocation()
  const path = location.pathname
  const invoiceId = location.state?.id

  const [invoice, setInvoice] = useState({ lines: [] })
  const [company, setCompany] = useState({})
  const [img, setImg] = useState('')
  const [settings, setSettings] = useState({})
  const [isSubmit, setIsSubmit] = useState(false)

  const fileReaderRef = useRef(null)

  const toolbarOptions = useMemo(
    () => ({
      title: path.includes('/add') ? 'Nouvelle Facture' : `Facture ${invoice.invoice_number ?? ''}`
    }),
    [invoice.invoice_number, path]
  )

  const mode = useMemo(() => {
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path])

  const setLines = useCallback((payload) => {
    setInvoice((prev) => ({ ...prev, lines: payload }))
  }, [])

  const invoiceQuery = mode === 'edit' ? { key: 'invoice', fn: getObject, args: ['invoices', invoiceId] } : null

  const queries = [
    { key: 'settings', fn: getObjectsWithFilter, args: ['settings', { company_id: user.company_id }] },
    { key: 'company', fn: getObject, args: ['company', user.company_id] },
    invoiceQuery
  ].filter(Boolean)

  const { data: fetched, loading: loadingEntities } = useFetchEntities(queries)

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'invoices',
    mode,
    id: invoiceId,
    user,
    settingsKey: 'invoice_number',
    counterName: 'invoices_nb',
    updateCounter: true,
    editFn: editObject,
    contractLineName: 'Invoices',
    args: [invoice],
    onSuccessRedirect: (id) => history(location.state?.history || ROUTES.VIEW, { state: { id } }),
    onSuccess: (res) => {
      setInvoice(res.data)
      convertDocumentAndUploadFile(
        <InvoicePDF invoice_param={res.data} company_param={company} logo_param={img} settings={settings} />,
        `facture_${res.data.invoice_number}`,
        user,
        'invoice',
        'invoices',
        res.data
      )
      setIsSubmit(false)
    },
    onError: () => setIsSubmit(false),
    buildPayload: buildInvoicePayload
  })

  const memoizedFields = useMemo(() => getInvoiceFields(user, location, setLines), [user, location, setLines])

  const options = useMemo(() => ({ fields: memoizedFields, data: invoice }), [memoizedFields, invoice])

  useEffect(() => {
    if (loadingEntities) return

    if (fetched.settings) setSettings(fetched.settings)

    if (fetched.company) {
      setCompany(fetched.company)
      const attach = fetched.company.attachments?.find((a) => a.type === 'logo')
      if (attach?.file?.data) {
        const blob = new Blob([new Uint8Array(attach.file.data)])
        const reader = new FileReader()
        fileReaderRef.current = reader
        reader.onloadend = () => {
          const base64 = reader.result?.toString().split(',')[1] ?? ''
          setImg(base64)
          fileReaderRef.current = null
        }
        reader.readAsDataURL(blob)
      }
    }

    if (fetched.invoice) {
      setInvoice(fetched.invoice)
    }

    return () => {
      if (fileReaderRef.current && fileReaderRef.current.readyState === 1) {
        try {
          fileReaderRef.current.onloadend = null
          fileReaderRef.current = null
        } catch (e) {
          /* ignore */
        }
      }
    }
  }, [fetched, loadingEntities])

  const totalAmount = useMemo(() => {
    return (Array.isArray(invoice.lines) ? invoice.lines : []).reduce((sum, line) => {
      const qty = parseFloat(line.quantity) || 0
      const price = parseFloat(line.unit_price) || 0
      return sum + qty * price
    }, 0)
  }, [invoice.lines])

  useEffect(() => {
    setInvoice((prev) => {
      if ((prev.total_amount_net || 0) === totalAmount) return prev
      return { ...prev, total_amount_net: totalAmount }
    })
  }, [totalAmount])

  useEffect(() => {
    if (isSubmit) {
      const cleanedInvoice = {
        ...invoice,
        lines: (invoice.lines || []).map((line) => ({
          ...line,
          priceBook_id: line.priceBook_id < -1 ? -1 : line.priceBook_id
        }))
      }
      saveEntity({ data: cleanedInvoice })
    }
  }, [isSubmit])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 65px)', overflowY: 'auto', flex: 1 }}>
      <Toolbar options={toolbarOptions} object={invoice} />

      <Form
        options={options}
        mode='edit'
        setDataParent={setInvoice}
        setIsSubmitParent={setIsSubmit}
        loading={loading}
      />
    </Box>
  )
}

export default EditInvoice
